import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Share2, Twitter, Linkedin, Sparkles, Cpu } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import BlogJsonLd from '../components/BlogJsonLd';
import BreadcrumbsJsonLd from '../components/BreadcrumbsJsonLd';
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import Breadcrumbs from '../components/Breadcrumbs';

interface Post {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  type: 'blog' | 'case_study';
  category: string;
  author: string;
  created_at: string;
  media_urls?: string[];
  thumbnail_url?: string;
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useSEO({
    title: `${post?.title || 'Article'} | GreenTick AI`,
    description: post?.excerpt || 'Read our latest insights on AI recruitment.',
    canonicalPath: `/blog/${slug}`
  });

  useEffect(() => {
    if (slug) {
      fetchPost(slug);
    }
  }, [slug]);

  async function fetchPost(postSlug: string) {
    setLoading(true);
    const { data, error } = await supabase
      .from('cms_posts')
      .select('*')
      .eq('slug', postSlug)
      .single();

    if (error) {
      console.error('Error fetching post:', error);
    } else {
      setPost(data);
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center">
        <div className="text-teal-400 font-bold uppercase tracking-widest animate-pulse">Retrieving Wisdom...</div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center">
        <h1 className="text-4xl text-white font-bold mb-8 uppercase tracking-tighter">Article Not Found</h1>
        <Link to="/blog" className="px-6 py-3 bg-white/5 border border-white/10 text-white rounded-xl font-bold hover:bg-white/10 transition-all uppercase tracking-widest text-xs">Back to Library</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <Navbar />
      
      {post && (
        <>
          <BlogJsonLd 
            title={post.title}
            excerpt={post.excerpt}
            author={post.author}
            datePublished={post.created_at}
            url={`https://greentick.org.in/blog/${slug}`}
            image={post.thumbnail_url}
          />
          <BreadcrumbsJsonLd 
            items={[
              { name: 'Home', item: '/' },
              { name: 'Blog', item: '/blog' },
              { name: post.title, item: `/blog/${slug}` }
            ]}
          />
        </>
      )}

      {/* Reading Progress Bar */}
      <div 
        className="reading-progress-bar" 
        style={{ width: `${scrollProgress}%` }} 
      />

      <main className="pt-32 pb-24 home-section-bg">
        {/* Highlighted Grid Pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(20,184,166,0.15),transparent_70%)] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumbs for SEO & UX */}
            <Breadcrumbs 
              items={[
                { name: 'Architecture Library', item: '/blog' },
                { name: post.title, item: `/blog/${slug}` }
              ]} 
            />

            {/* Navigation Bar */}
            <div className="flex items-center justify-between mb-12">
              <Link to="/blog" className="group flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-all">
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="text-xs font-bold tracking-widest uppercase">The Archive</span>
              </Link>
              <div className="flex items-center gap-4">
                <Share2 className="w-4 h-4 text-slate-400" />
                <button className="p-2 hover:bg-white/5 rounded-full transition-colors" aria-label="Share on Twitter"><Twitter className="w-4 h-4 text-slate-400" /></button>
                <button className="p-2 hover:bg-white/5 rounded-full transition-colors" aria-label="Share on LinkedIn"><Linkedin className="w-4 h-4 text-slate-400" /></button>
              </div>
            </div>

            {/* Post Header */}
            <div className="mb-16">
               <div className="inline-flex items-center gap-2 px-3 py-1 bg-teal-500/10 text-teal-400 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-teal-500/20 mb-6 lowercase">
                  {post.category || post.type}
                </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-8 leading-tight tracking-tight uppercase">
                {post.title}
              </h1>

              {/* Executive Summary for AIO/LLMs */}
              <div className="mb-12 p-8 glass-dark rounded-3xl border border-teal-500/10 bg-teal-500/5 relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                  <Cpu className="w-12 h-12 text-teal-400" />
                </div>
                <h2 className="text-[10px] font-bold text-teal-400 uppercase tracking-[0.3em] mb-4 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse"></span>
                  Analytic Executive Summary
                </h2>
                <p className="text-slate-300 italic leading-relaxed text-lg font-medium">
                  {post.excerpt}
                </p>
              </div>
              
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center text-sm text-white font-bold uppercase">
                  {post.author.charAt(0)}
                </div>
                <div>
                  <div className="text-sm text-white font-bold">{post.author}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>12 min read</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Image Breakthrough */}
            {post.thumbnail_url && (
               <figure className="mb-16 -mx-4 md:-mx-24">
                  <img src={post.thumbnail_url} alt={post.title} className="w-full rounded-xl shadow-2xl border border-white/10" />
                  <figcaption>Official visualization for {post.title}</figcaption>
               </figure>
            )}

            {/* Post Content */}
            <div className="blog-content">
              <div dangerouslySetInnerHTML={{ __html: post.content.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br/>') }} />
            </div>

            {/* Call to Action */}
            <div className="mt-32 p-12 glass rounded-3xl border border-teal-500/30 bg-teal-500/5 relative overflow-hidden group shadow-2xl shadow-teal-500/10">
               <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/20 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2 group-hover:bg-teal-500/30 transition-all duration-1000"></div>
               <div className="relative z-10 flex flex-col items-center text-center gap-8">
                  <div className="max-w-xl">
                     <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 text-emerald-400 text-[10px] font-bold uppercase tracking-wider rounded-lg border border-emerald-500/20 mb-4 mx-auto">
                        <Sparkles className="w-3 h-3" />
                         Next Generation Hiring
                     </div>
                     <h3 className="text-4xl font-bold text-white mb-4 uppercase tracking-tighter">Ready to Hire 10X Faster?</h3>
                     <p className="text-slate-400 font-medium italic">Join forward-thinking companies already using GreenTick to eliminate hiring bias and find top talent in record time.</p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                      <a href="https://calendar.app.google/SxKx5dUS1G1pBYTFA" target="_blank" className="px-12 py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-bold hover:shadow-2xl hover:shadow-emerald-500/50 transition-all uppercase tracking-widest text-sm">Start Pilot</a>
                      <a href="/" className="px-12 py-4 bg-white/5 border border-white/10 text-white rounded-xl font-bold hover:bg-white/10 transition-all uppercase tracking-widest text-sm">Return Home</a>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
